import axiosInstanceApiGateway from 'config/apiServiceApiGateway'
import { Get } from 'config/apiServiceConfig'
import { queryObjToString } from 'utils/queryParamsURL'

const { Get: GetGw, Post: PostGw, Patch: PatchGw } = axiosInstanceApiGateway

const ROUTE_WAVE_BASE = '/wims/internal/v1/route-waves'

/* ---------------------------------------------------------------- */
/* Route Wave Service                                                */
/* ---------------------------------------------------------------- */

export type RouteWaveConstantOptionType = {
  key: string
  label: string
}

export type RouteWaveConstantsType = {
  distance_types: RouteWaveConstantOptionType[]
  trip_types: RouteWaveConstantOptionType[]
  rit: RouteWaveConstantOptionType[]
  wave: RouteWaveConstantOptionType[]
}

export type RouteWaveLocationRefType = {
  id: number
  name: string
  type: string
}

export type RouteWaveListItemType = {
  id: number
  name: string
  rit: number
  wave: number
  location: RouteWaveLocationRefType
  distance_type: string
  trip_type: string
  departure_time: string
  active: boolean
  destinations: string
}

export type RouteWaveDestinationDetailType = {
  id: number
  location: RouteWaveLocationRefType
  estimated_arrival_time: string
}

export type RouteWaveDetailType = {
  id: number
  name: string
  rit: number
  wave: number
  location: RouteWaveLocationRefType
  distance_type: string
  trip_type: string
  departure_time: string
  active: boolean
  route_wave_destinations: RouteWaveDestinationDetailType[]
}

export type RouteWavePaginationType = {
  sort: string
  direction: 'asc' | 'desc'
  page_size: number
  page_index: number
}

export type ListRouteWavesQueryType = {
  name?: string
  active?: boolean
  location_id?: number
  rit?: number
  sort?: string
  direction?: 'asc' | 'desc'
  page_size?: number
  page_index?: number
}

export type CreateRouteWaveDestinationPayloadType = {
  location_id: number
  estimated_arrival_time: string
}

export type CreateRouteWavePayloadType = {
  name: string
  rit: number
  wave: number
  location_id: number
  distance_type: string
  trip_type: string
  departure_time: string
  max_so_created_time: string
  destinations: CreateRouteWaveDestinationPayloadType[]
}

export type UpdateRouteWaveDestinationPayloadType = {
  id: number
  location_id: number
  sequence: number
  estimated_arrival_time: string
}

export type UpdateRouteWavePayloadType = Partial<{
  active: boolean
  name: string
  rit: number
  wave: number
  location_id: number
  distance_type: string
  trip_type: string
  departure_time: string
  max_so_created_time: string
  delete_route_wave_destination_ids: number[]
  update_route_wave_destinations: UpdateRouteWaveDestinationPayloadType[]
}>

export type CreateRouteWaveResponseType = { id: number }

/** Sentinel timestamp used for `max_so_created_time` — FE does not surface this field. */
export const MAX_SO_CREATED_TIME_SENTINEL = '2099-01-01T00:00:00Z'

const TIME_HHMM_REGEX = /(\d{1,2}):(\d{2})/
const OFFSET_REGEX = /([+-])(\d{2}):?(\d{2})/

/**
 * Extracts an `HH:MM` value from any backend time string and converts it to WIB (UTC+7).
 * Offset in the raw string (e.g. `+0000`, `+07:00`) is used for conversion.
 * Returns `''` when no time component is found — safe for `<input type="time">`.
 */
export const extractHHMM = (raw?: string): string => {
  if (!raw) return ''
  const timeMatch = TIME_HHMM_REGEX.exec(raw)
  if (!timeMatch) return ''

  let totalMinutes = parseInt(timeMatch[1], 10) * 60 + parseInt(timeMatch[2], 10)

  const offsetMatch = OFFSET_REGEX.exec(raw)
  if (offsetMatch) {
    const sign = offsetMatch[1] === '+' ? 1 : -1
    const offsetMinutes = sign * (parseInt(offsetMatch[2], 10) * 60 + parseInt(offsetMatch[3], 10))
    totalMinutes = (((totalMinutes - offsetMinutes + 7 * 60) % (24 * 60)) + 24 * 60) % (24 * 60)
  }

  const h = Math.floor(totalMinutes / 60)
    .toString()
    .padStart(2, '0')
  const m = (totalMinutes % 60).toString().padStart(2, '0')
  return `${h}:${m}`
}

export type DownloadRouteWaveCsvParamsType = {
  location_id?: number
  active?: boolean
}

export const listRouteWaves = (query: ListRouteWavesQueryType) =>
  GetGw<RouteWaveListItemType[]>({
    url: ROUTE_WAVE_BASE,
    params: query,
  })

export const getRouteWaveConstants = () =>
  GetGw<RouteWaveConstantsType>({
    url: `${ROUTE_WAVE_BASE}/constants`,
  })

export const getRouteWaveDetail = (id: number | string) =>
  GetGw<RouteWaveDetailType>({
    url: `${ROUTE_WAVE_BASE}/${id}`,
  })

export const createRouteWave = (payload: CreateRouteWavePayloadType) =>
  PostGw<CreateRouteWaveResponseType, true>({
    url: ROUTE_WAVE_BASE,
    data: payload,
  })

export const updateRouteWave = (id: number | string, payload: UpdateRouteWavePayloadType) =>
  PatchGw<Record<string, never>, true>({
    url: `${ROUTE_WAVE_BASE}/${id}`,
    data: payload,
  })

/**
 * Streamed CSV endpoint. Backend returns concatenated JSON chunks of shape
 * `{ "chunk_data": "<binary>" }`. Caller concatenates them via {@link parseStreamedCsvChunks}.
 */
export const downloadRouteWaveCsv = (params: DownloadRouteWaveCsvParamsType) =>
  GetGw<string, true>({
    url: `${ROUTE_WAVE_BASE}:download`,
    params,
    config: { responseType: 'text', transformResponse: [(r: string) => r] },
  })

const CHUNK_REGEX = /"chunk_data"\s*:\s*"((?:\\.|[^"\\])*)"/g

export const parseStreamedCsvChunks = (raw: string): string => {
  const parts: string[] = []
  CHUNK_REGEX.lastIndex = 0
  let match = CHUNK_REGEX.exec(raw)
  while (match !== null) {
    try {
      parts.push(JSON.parse(`"${match[1]}"`))
    } catch {
      parts.push(match[1])
    }
    match = CHUNK_REGEX.exec(raw)
  }
  return parts.join('')
}

/* ---------------------------------------------------------------- */
/* Location & Vendor lookup (separate services, still needed by form) */
/* ---------------------------------------------------------------- */

export type LocationType = [
  'HUB',
  'MEGAHUB',
  'OFFICE',
  'VENDING MACHINE',
  'VENDOR',
  'VIRTUAL',
  'WAREHOUSE',
]

export type LocationDetailType = {
  location_id: number
  location_name: string
  location_address: string
  supervisor_names: Nullable<string>
  location_detail: string
  location_type: LocationType[number]
  location_active: boolean
  location_flag: string
}

export type GetLocationResponseType = {
  content: LocationDetailType[]
}

export type LocationQueryType = {
  type: LocationType[number]
  name: string
}

export type VendorType = {
  id: number
  companyName: string
  categoryVendor: string
  creditLimit: string
  waitingTime: number
  status: boolean
}

export type VendorQueryType = {
  companyName: string
}

export type GetVendorResponseType = {
  data: VendorType[]
}

export const getLocation = (query: LocationQueryType) =>
  Get<GetLocationResponseType>({
    url: '/api/location',
    params: { ...query, pageSize: 1000 },
  })

export const getVendor = (query: VendorQueryType) =>
  Get<GetVendorResponseType>({
    url: '/api/vendor',
    params: { ...query, pageSize: 1000 },
  })

/* ---------------------------------------------------------------- */
/* Log history (legacy detail endpoint — temporary until new logs    */
/* endpoint is provided on the route-wave service)                   */
/* ---------------------------------------------------------------- */

export type RouteMasterLogType = {
  notes: string
  createdAt: number
  createdByName: string
  createdByEmail: string
}

export type GetRouteMasterDetailResponseType = {
  data: {
    logs: RouteMasterLogType[]
  }
}

export const getRouteMasterDetail = (id: number | string) =>
  Get<GetRouteMasterDetailResponseType>({
    url: `/api/wms/:version/route-master/:id`,
    version: 'v1',
    urlParams: { id },
  })

/* ---------------------------------------------------------------- */
/* @deprecated Used only by SupplyOrder feature. Will be removed     */
/* once SupplyOrder migrates to the route-wave service.              */
/* ---------------------------------------------------------------- */

export type RouteAndScheduleQueryType = {
  ids: number[]
  name: string
  originETD: string
  originLocationIDs: number[]
  originVendorIDs: number[]
  distanceType: string
  sort: 'name' | 'distance_type'
  direction: 'asc' | 'desc'
  pageIndex: string
  pageSize: string
}

export type LegacyRouteOriginType = {
  id: number
  locationType: string
  locationID: number
  locationName: string
  estimatedTimeOfArrival: string
  estimatedTimeOfDeparture: string
}

export type LegacyRouteAndScheduleType = {
  id: number
  name: string
  distanceType: string
  tripType: string
  active: boolean
  createdAt?: string
  maxSoTime?: string
  routeOrigins: LegacyRouteOriginType[]
  routeDestinations: LegacyRouteOriginType[]
}

export type GetRouteAndScheduleResponseType = {
  data: LegacyRouteAndScheduleType[]
  message: string
  pagination: null
}

export const getRouteAndQueryList = (query: Partial<RouteAndScheduleQueryType>) =>
  Get<GetRouteAndScheduleResponseType>({
    url: `/api/wms/:version/route-master${queryObjToString(query)}`,
    version: 'v1',
  })
